import React, { useState } from "react";
import { SecondaryBlueButton } from "@velaro/velaro-component-lib";
import { ScraperJob, ScrapedPage } from "./types";

interface ScraperJobResultsModalProps {
  job: ScraperJob;
  results: ScrapedPage[];
  onClose: () => void;
}

export default function ScraperJobResultsModal({
  job,
  results,
  onClose
}: ScraperJobResultsModalProps) {
  const [expandedIndex, setExpandedIndex] = useState<number | null>(null);
  const [filter, setFilter] = useState<"all" | "success" | "failed">("all");

  const filteredResults = results.filter(r => {
    if (filter === "all") return true;
    if (filter === "success") return r.success;
    if (filter === "failed") return !r.success;
    return true;
  });

  const successCount = results.filter(r => r.success).length;
  const failedCount = results.filter(r => !r.success).length;

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleString();
  };

  const getContentPreview = (content: string, maxLength = 200) => {
    if (content.length <= maxLength) return content;
    return content.substring(0, maxLength) + "...";
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg shadow-xl w-full max-w-4xl max-h-[90vh] flex flex-col">
        <div className="p-4 border-b flex-shrink-0">
          <div className="flex justify-between items-start">
            <div>
              <h2 className="text-lg font-semibold">{job.name}</h2>
              <p className="text-sm text-slate-500">
                Job ID: {job.job_id} | Created: {formatDate(job.created_at)}
              </p>
            </div>
            <button
              onClick={onClose}
              className="text-slate-400 hover:text-slate-600 text-2xl"
            >
              &times;
            </button>
          </div>
          <div className="flex gap-4 mt-4">
            <div className="bg-slate-100 px-3 py-2 rounded">
              <div className="text-sm text-slate-500">Total Pages</div>
              <div className="text-xl font-semibold">{results.length}</div>
            </div>
            <div className="bg-green-50 px-3 py-2 rounded">
              <div className="text-sm text-green-600">Successful</div>
              <div className="text-xl font-semibold text-green-700">{successCount}</div>
            </div>
            <div className="bg-red-50 px-3 py-2 rounded">
              <div className="text-sm text-red-600">Failed</div>
              <div className="text-xl font-semibold text-red-700">{failedCount}</div>
            </div>
          </div>
          <div className="flex gap-2 mt-4">
            <button
              onClick={() => setFilter("all")}
              className={`px-3 py-1 rounded text-sm ${
                filter === "all"
                  ? "bg-blue-100 text-blue-700"
                  : "bg-slate-100 text-slate-600 hover:bg-slate-200"
              }`}
            >
              All ({results.length})
            </button>
            <button
              onClick={() => setFilter("success")}
              className={`px-3 py-1 rounded text-sm ${
                filter === "success"
                  ? "bg-green-100 text-green-700"
                  : "bg-slate-100 text-slate-600 hover:bg-slate-200"
              }`}
            >
              Success ({successCount})
            </button>
            <button
              onClick={() => setFilter("failed")}
              className={`px-3 py-1 rounded text-sm ${
                filter === "failed"
                  ? "bg-red-100 text-red-700"
                  : "bg-slate-100 text-slate-600 hover:bg-slate-200"
              }`}
            >
              Failed ({failedCount})
            </button>
          </div>
        </div>
        <div className="flex-1 overflow-y-auto p-4">
          {filteredResults.length === 0 ? (
            <div className="text-center py-8 text-slate-500">
              No results match the selected filter
            </div>
          ) : (
            <div className="space-y-3">
              {filteredResults.map((result, index) => (
                <div
                  key={result.url}
                  className={`border rounded-lg overflow-hidden ${
                    result.success ? "border-slate-200" : "border-red-200"
                  }`}
                >
                  <div
                    className={`p-3 cursor-pointer hover:bg-slate-50 flex items-center justify-between ${
                      result.success ? "bg-white" : "bg-red-50"
                    }`}
                    onClick={() => setExpandedIndex(expandedIndex === index ? null : index)}
                  >
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2">
                        <span
                          className={`w-2 h-2 rounded-full ${
                            result.success ? "bg-green-500" : "bg-red-500"
                          }`}
                        />
                        <span className="font-medium text-sm truncate">
                          {result.title || result.url}
                        </span>
                      </div>
                      <div className="text-xs text-slate-500 truncate mt-1">
                        {result.url}
                      </div>
                    </div>
                    <div className="flex items-center gap-4 flex-shrink-0 ml-4">
                      <div className="text-xs text-slate-500">
                        {result.word_count} words
                      </div>
                      <div className="text-xs px-2 py-1 rounded bg-slate-100">
                        {result.content_type}
                      </div>
                      {result.is_faq && (
                        <div className="text-xs px-2 py-1 rounded bg-purple-100 text-purple-700">
                          FAQ
                        </div>
                      )}
                      <span className="text-slate-400">
                        {expandedIndex === index ? "▼" : "▶"}
                      </span>
                    </div>
                  </div>
                  {expandedIndex === index && (
                    <div className="p-3 border-t bg-slate-50">
                      <div className="grid grid-cols-3 gap-4 mb-3 text-xs">
                        <div>
                          <span className="text-slate-500">Characters:</span>{" "}
                          <span className="font-medium">{result.char_count}</span>
                        </div>
                        <div>
                          <span className="text-slate-500">Words:</span>{" "}
                          <span className="font-medium">{result.word_count}</span>
                        </div>
                        <div>
                          <span className="text-slate-500">Scraped:</span>{" "}
                          <span className="font-medium">{formatDate(result.scraped_at)}</span>
                        </div>
                      </div>
                      {result.is_faq && result.faq_pairs && result.faq_pairs.length > 0 && (
                        <div className="mb-3">
                          <div className="text-sm font-medium mb-2">
                            FAQ Pairs ({result.faq_pairs.length})
                          </div>
                          <div className="space-y-2">
                            {result.faq_pairs.slice(0, 3).map((pair, i) => (
                              <div key={i} className="bg-white p-2 rounded text-sm">
                                <div className="font-medium text-blue-700">Q: {pair.question}</div>
                                <div className="text-slate-600 mt-1">A: {getContentPreview(pair.answer, 150)}</div>
                              </div>
                            ))}
                            {result.faq_pairs.length > 3 && (
                              <div className="text-xs text-slate-500">
                                + {result.faq_pairs.length - 3} more pairs
                              </div>
                            )}
                          </div>
                        </div>
                      )}
                      <div>
                        <div className="text-sm font-medium mb-2">Content Preview</div>
                        <div className="bg-white p-3 rounded text-sm max-h-48 overflow-y-auto whitespace-pre-wrap">
                          {result.content || "(No content)"}
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>
        <div className="p-4 border-t flex-shrink-0 flex justify-end">
          <SecondaryBlueButton label="Close" onClick={onClose} />
        </div>
      </div>
    </div>
  );
}
